// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// DRAFT AI-assistance notice (IP, License & Attribution Policy v1.3 §12d-1):
// This content was prepared with the assistance of AI tools and is, on this
// branch, a DRAFT pending human review. On the human-approved merge of its
// Merge Request this notice flips to the published variant (§12d-2).
// Bu icerik yapay zeka destekli araclarla hazirlanmis bir TASLAKTIR (§12d-1);
// insan onayiyla birlestirilmesinde yayimlanmis surumune (§12d-2) donusur.

/**
 * Bridge error taxonomy (adapted from AU0008 ADR-005 D6).
 *
 * Failures on the widget <-> Widget API Gateway reply channel are surfaced as a
 * structured, typed error rather than a raw thrown platform object. Each kind is
 * fixed here so the widget handles them without a later protocol change.
 *
 *   - malformed_message   : envelope/record validation failed (missing/typed-
 *                           wrong field).
 *   - version_mismatch     : incompatible protocolVersion (defined; emitted only
 *                           once Gateway-side versioning is adopted).
 *   - function_not_allowed : requested module.function not on a Gateway allow-
 *                           list (defined; emitted only once that lands).
 *   - unknown_function     : the pair does not resolve to a callable
 *                           ZOHO.CREATOR function at runtime.
 *   - system_error         : the invoked ZOHO.CREATOR function itself failed;
 *                           carries the platform reason in structured form. The
 *                           server-side resolver driving the record to the Error
 *                           terminal state also surfaces here (its first_error).
 *   - timeout              : no valid reply within the per-request timeout, or
 *                           the record did not reach a terminal state within the
 *                           bounded poll window (ADR-002 P4 bounded give-up).
 */

/** The closed set of bridge error kinds. */
export const BRIDGE_ERROR_KINDS = [
  "malformed_message",
  "version_mismatch",
  "function_not_allowed",
  "unknown_function",
  "system_error",
  "timeout",
] as const;

export type BridgeErrorKind = (typeof BRIDGE_ERROR_KINDS)[number];

/**
 * A structured bridge error. Declarative and non-raw: `kind` is machine-
 * readable; `message` is a human-readable English summary that must not carry
 * device secrets or raw payload bytes; `detail` optionally carries a platform-
 * reported, already-sanitised reason string (e.g. the record's first_error).
 */
export interface BridgeError {
  readonly kind: BridgeErrorKind;
  readonly message: string;
  readonly detail?: string;
}

/** Construct a BridgeError without ever embedding a raw platform object. */
export function makeBridgeError(
  kind: BridgeErrorKind,
  message: string,
  detail?: string,
): BridgeError {
  return detail === undefined ? { kind, message } : { kind, message, detail };
}

/** Type guard: a plain object that is a well-formed BridgeError. */
export function isBridgeError(value: unknown): value is BridgeError {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v["kind"] === "string" &&
    (BRIDGE_ERROR_KINDS as readonly string[]).includes(v["kind"]) &&
    typeof v["message"] === "string" &&
    (v["detail"] === undefined || typeof v["detail"] === "string")
  );
}
